/**
 * useAuth — React Query hooks for all authentication operations.
 *
 *  useLogin()   — POST /auth/login
 *  useGetMe()   — GET  /auth/me  (auto-fires when token is present)
 *  useLogout()  — POST /auth/logout
 *
 * Navigation and toast feedback live here so the pages/components
 * that consume these hooks stay thin.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { loginApi, getMeApi, logoutApi } from "../api/authApi";
import { useAuthStore } from "../store/authStore";
import { LoginFormType } from "../types/login";
import { AuthUserProps } from "../types/auth";
import { AxiosError } from "axios";

// ─── Query key factory ────────────────────────────────────────────────────────

export const AUTH_QUERY_KEYS = {
  me: ["auth", "me"] as const,
} as const;

// ─── useLogin ─────────────────────────────────────────────────────────────────

export function useLogin() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginFormType) => loginApi(credentials),

    onSuccess: (data) => {
      // Store tokens in cookies via loginSuccess (expiry set from API response)
      loginSuccess({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        refreshTokenExpiresIn: data.refresh_token_expires_in,
      });

      // Clear any stale user cache from a previous session
      queryClient.removeQueries({ queryKey: AUTH_QUERY_KEYS.me });

      toast.success("Logged in successfully");
      navigate("/statistics");
    },

    onError: (
      error: AxiosError & {
        response: { data: { detail?: string; message?: string } };
      },
    ) => {
      toast.error(
        error.response?.data?.detail || "Login failed. Please try again.",
      );
    },
  });
}

// ─── useGetMe ─────────────────────────────────────────────────────────────────

/**
 * Fetches the current user profile from /auth/me.
 * Only runs when an access token exists.
 * React Query caches the result for 5 minutes; background refetches keep
 * it fresh on window focus and reconnect.
 */
export function useGetMe() {
  const { authToken } = useAuthStore();

  return useQuery<AuthUserProps, Error>({
    queryKey: AUTH_QUERY_KEYS.me,
    queryFn: getMeApi,
    enabled: !!authToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

// ─── useLogout ────────────────────────────────────────────────────────────────

/**
 * Calls POST /auth/logout on the server (best-effort) then always clears
 * local auth state so the user is logged out even if the API request fails.
 */
export function useLogout() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logoutApi,

    onSettled: () => {
      // Always clean up regardless of server response
      logout();
      queryClient.clear();
      toast.info("Logged out successfully");
      navigate("/users/sign_in");
    },
  });
}
